<template>
    <tr :style="tableRowStyle">
        <template v-if="isType('result')">
            <td v-if="index === 0"
                class="region"
                :rowspan="rowspan"
                :class="region ? 'region' : 'no-region'"
                v-text="region || uLang('app.no_region')"
            >
            </td>
            <td class="bold" :class="resultClass">
                <link-if-permissions
                    :to="{ name: 'admin.relations.view', params: { id: coach.id }}"
                    permissions="view-relations"
                    v-text="coach.full_name"
                />
            </td>
        </template>

        <template v-else>
            <td class="total-first-row" :class="resultClass" colspan="2" v-text="rowName"></td>
        </template>

        <td class="result"
            :class="resultClass"
            :style="clickableStyle"
            @click="showModal('canceled')"
        >
            {{ getCount('canceled') }}
        </td>

        <td class="result"
            :class="resultClass"
            :style="clickableStyle"
            @click="openStartedTrajectoriesModal = isClickable"
            v-text="getCount('startedTrajectories')"
        >
        </td>

        <td class="result"
            :class="resultClass"
            :style="clickableStyle"
            @click="showModal('completed')"
            v-text="getCount('completed')"
        >
        </td>

        <td class="result"
            :class="resultClass"
            :style="clickableStyle"
            @click="showModal('completedUnsuccessfully')"
            v-text="getCount('completedUnsuccessfully')"
        >
        </td>

        <td class="result"
            :class="resultClass"
            :style="clickableStyle"
            @click="showModal('completedSuccessfully')"
            v-text="getCount('completedSuccessfully')"
        >
        </td>

        <td class="result"
            :class="resultClass"
            :style="clickableStyle"
            @click="showModal('completedAtTheExpenseOfTheClient')"
            v-text="getCount('completedAtTheExpenseOfTheClient')"
        >
        </td>

        <td class="divider"></td>

        <td class="success-rate"
            :class="resultClass"
            v-text="successRate !== null ? `${ successRate }%` : '-'"
        >
        </td>

        <td class="result"
            :class="resultClass"
            :style="clickableStyle"
            @click="showModal('started')"
            v-text="getCount('started')"
        >
        </td>

        <portal v-if="isShowingModal" to="modals">
            <coach-result-candidates-modal
                v-for="(status, key) in statuses"
                v-model="statuses[key].showModal"
                @hidden="statuses[key].showModal = false"
                :key="status.value"
                :enrolments="$data[key]"
            />
            <coach-result-candidates-modal
                v-model="openStartedTrajectoriesModal"
                @hidden="openStartedTrajectoriesModal = false"
                :enrolments="startedTrajectories"
            />
        </portal>
    </tr>
</template>

<script>
    import { mapGetters } from 'vuex'
    import CoachEnrolmentsModal from "../../modals/coach-enrolments-modal"
    import CoachResultCandidatesModal from "../../modals/coach-result-candidates-modal"

    export default {
        name: "management-info-result-row",
        components: { CoachResultCandidatesModal, CoachEnrolmentsModal },
        props: {
            rowType: {
                type: String,
                default: 'result'
            },
            rowName: {
                type: String,
                default: null,
            },
            coach: {
                type: Object,
            },
            coaches: {
                type: Array,
            },
            region: {
                type: String,
            },
            index: {
                type: Number,
            },
            rowspan: {
                type: Number,
            },
            date_from: {
                type: Date,
                required: false
            },
            date_to: {
                type: Date,
                required: false
            },
        },
        data() {
            return {
                notYetStarted: [],
                started: [],
                canceled: [],
                completed: [],
                completedSuccessfully: [],
                completedUnsuccessfully: [],
                completedAtTheExpenseOfTheClient: [],
                startedTrajectories: [],
                openStartedTrajectoriesModal: false,
                statuses: {
                    notYetStarted: {
                        value: 'not_yet_started',
                        showModal: false,
                    },
                    started: {
                        value: 'started',
                        showModal: false,
                    },
                    canceled: {
                        value: 'canceled',
                        showModal: false,
                    },
                    completed: {
                        value: 'completed',
                        showModal: false,
                    },
                    completedSuccessfully: {
                        value: 'completed_successfully',
                        showModal: false,
                    },
                    completedUnsuccessfully: {
                        value: 'completed_unsuccessfully',
                        showModal: false,
                    },
                    completedAtTheExpenseOfTheClient: {
                        value: 'completed_at_the_expense_of_the_client',
                        showModal: false,
                    },
                },
            }
        },
        computed: {
            ...mapGetters(['enrolmentStatus', 'authId']),
            successRate() {
                if (this.isType('average')) {
                    return null
                }

                let total = this.getCount('completedSuccessfully') + this.getCount('completedUnsuccessfully')

                return total ? ((this.getCount('completedSuccessfully') / total) * 100).toFixed(0) : 0
            },
            resultClass() {
                if (!this.isType('result')) {
                    return 'bold'
                }
            },
            isShowingModal() {
                return Object.values(this.statuses).some(s => s.showModal) || this.openStartedTrajectoriesModal
            },
            isClickable() {
                return (this.authAdmin || $hasRole('super_admin')) || (this.coach && this.coach.id === this.authId)
            },
            clickableStyle() {
                return this.isClickable ? 'cursor: pointer; color: #c10f6d' : ''
            },
            tableRowStyle() {
                return this.isType('result') && parseInt(this.coach.collaboration_ended) ? 'background-color: lightgray' : ''
            }
        },
        methods: {
            recalculate() {
                for (status in this.statuses) {
                    switch (this.rowType) {
                        case 'result':
                            this[status] = this.countTrajectoryEnrolmentStatus(this.statuses[status])
                            break
                        case 'average':
                        case 'total':
                        case 'grandTotal':
                            this[status] = this.getForStatus(status)
                            break
                    }
                }

                this.calculateStartedTrajectories()
            },
            averageForCoach(amount) {
                return parseInt(((amount || 0) / this.coaches.length).toFixed(0)) || 0
            },
            calculateStartedTrajectories() {
                switch (this.rowType) {
                    case 'result':
                        this.startedTrajectories = this.getStartedTrajectories()
                        break
                    case 'average':
                    case 'total':
                    case 'grandTotal':
                        this.startedTrajectories = this.coaches.reduce((acc, coach) => acc.concat(this.getStartedTrajectories(coach)), [])
                        break
                }
            },
            getCount(resultName) {
                const result = this[resultName]

                if (this.isType('average')) {
                    return this.averageForCoach(result ? result.length : 0)
                }

                return result ? result.length : 0
            },
            isType(type) {
                return this.rowType === type
            },
            getForStatus(status) {
                return this.coaches.reduce((acc, coach) => acc.concat(this.countTrajectoryEnrolmentStatus(this.statuses[status], coach)), [])
            },
            countTrajectoryEnrolmentStatus(status, coach = this.coach) {
                return coach.management_info_results.reduce((acc, cur) => cur.status_id === this.enrolmentStatus(status.value).id ? acc.concat(cur) : acc, []);
            },
            getStartedTrajectories(coach = this.coach) {
                let date_from = this.date_from ? moment(this.date_from) : null
                let date_to = this.date_to ? moment(this.date_to) : null

                return coach
                    .management_info_results
                    .reduce((acc, cur) => {
                        const start_date = moment(cur.start_date)

                        let isAfterFrom = date_from ? start_date.isSameOrAfter(date_from) : true
                        let isBeforeTo = date_to ? start_date.isSameOrBefore(date_to) : true

                        return isAfterFrom && isBeforeTo ? acc.concat(cur) : acc
                    }, [])
            },
            showModal(key) {
                if (this.isClickable) {
                    this.statuses[key].showModal = true
                }
            }
        },
        watch: {
            coaches() {
                this.recalculate()
            },
            coach() {
                this.recalculate()
            }
        },
        mounted() {
            this.recalculate()
        }
    }
</script>

<style scoped lang="scss">
    .region {
        background-color: white;
    }
    .total-first-row {
        text-align: right;
        font-weight: bold;
    }
    td {
        border: 1px solid black;
    }
    .result {
        text-align: center;
        font-size: larger;
    }
    .divider {
        visibility: hidden;
        border: none;
    }
    .bold {
        font-weight: bold;
    }
    .region {
        font-size: larger;
    }
    .no-region {
        font-size: larger;
        font-weight: bold;
        color: red;
    }
</style>
